import React, { JSXElementConstructor, memo, PropsWithChildren, useEffect, useState } from 'react';
import { Alert, Spinner } from '@cloudscape-design/components';
import { apiCall } from '../../sdpds/utils';
import { clusterInsightsDisabledVfis } from '../../constants';

type RenderClusterProps = PropsWithChildren<{
  resourceLoading: boolean;
  domainVersion?: string;
  message: string;
  domainIdentuple: string;
  addVfiCheck?: boolean;
  children: JSX.Element
}>;

const isVersionAtLeast = (givenVersion, baseVersion = '2.17.0') =>  {
    try {
        if (!givenVersion || typeof givenVersion !== "string") return false;

        var cleaned = givenVersion.replace(/[^\d.]/g, "");
        if (!cleaned || cleaned === "0" || cleaned === "0.0.0") return false;

        var givenParts = cleaned.split(".").map(function (n) {
        return parseInt(n, 10) || 0;
        });

        var baseParts = baseVersion.split(".").map(function (n) {
        return parseInt(n, 10) || 0;
        });

        var maxLength = Math.max(givenParts.length, baseParts.length);
        while (givenParts.length < maxLength) givenParts.push(0);
        while (baseParts.length < maxLength) baseParts.push(0);

        for (var i = 0; i < maxLength; i++) {
        if (givenParts[i] > baseParts[i]) return true;
        if (givenParts[i] < baseParts[i]) return false;
        }

        return true;
    } catch (err) {
        console.error("Version comparison failed:", givenVersion, baseVersion, err);
        return false;
    }
}

export const RenderClusterResourceSummary = memo(function RenderClusterResourceSummary({
  resourceLoading,
  domainVersion = '0.0.0',
  message,
  domainIdentuple,
  addVfiCheck = false,
  children,
}: RenderClusterProps) {
    const [updateDetails, setUpdateDetails] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isError, setIsError] = useState(false);
    const fetchUpdateDetails = async () => { 
        if (updateDetails !== null) {
            return;
        }
        setIsLoading(true);
        try {
            const [stackName, accountId, clusterName] = domainIdentuple?.split('/');
            const response = await apiCall('/softwareUpdateDetails/status', {
                cluster: {
                    stackName,
                    clusterId: `${accountId}:${clusterName}`
                }
            });
            if (response.statusCode === 200) {
                setUpdateDetails(response.body);
                setIsError(false);
            } else {
                setIsError(true);
                setUpdateDetails(null);
            }
        } catch (error) {
            setIsError(true);
            setUpdateDetails(null);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchUpdateDetails();
    }, [])

    if (resourceLoading || isLoading) {
        return (
        <div style={{ textAlign: 'center', marginTop: '2rem' }}>
            <Spinner size="large" />
        </div>
        );
    }

    if (!domainVersion || !isVersionAtLeast(domainVersion, '2.17.0')) {
        return <Alert type="info">{message}</Alert>;
    }

    if (addVfiCheck && updateDetails?.currentClusterReleaseVersion && clusterInsightsDisabledVfis.includes(updateDetails?.currentClusterReleaseVersion)) {
        return <Alert type="info">{`To use the this feature, you will need to upgrade your domain to OpenSearch Service software version 2.17 or later. Your domain is currently running version ${updateDetails?.currentClusterReleaseVersion}. Consider upgrading to the latest available version to access this and other new features`}</Alert>;
    }

    return (
        <>{children}</>
    );
});
